import React from 'react'
import axios from 'axios'
import moment from 'moment'
import { toast } from 'sonner'
import { LoaderCircle } from 'lucide-react'
import AvailabilityCalendar from '../availability_calendar/AvailabilityCalendar'
import '../availability_calendar/availability_calendar.css'
import store from '../../state/store'

// Per-candidate calendar modal for the sales portal — a faithful replica of the
// tutor portal's "Select times you're available for this job" modal
// (AvailabilityStep.svelte). Lesson view by default (client preferred + tutor
// availability background + lessons); "Edit Profile Availability" switches to
// editing the tutor's recurring availability, which persists via the sales API.

const DAY_NAMES = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const DAY_NAME_TO_ORD = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 7 }

// Saved job availability ([{day_of_week name, start_time, end_time}]) -> cells.
function savedTimesToCells(times) {
  const cells = new Set()
  ;(times || []).forEach((t) => {
    const ord = DAY_NAME_TO_ORD[String(t.day_of_week).toLowerCase()]
    if (!ord) return
    const s = parseHHMM(t.start_time)
    let e = parseHHMM(t.end_time)
    if (e <= s) e = 24 * 60
    for (let m = s; m < e; m += 15) cells.add(`${ord}:${m}`)
  })
  return cells
}

const fmtMin = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
const parseHHMM = (time) => {
  const [h, m] = String(time).split(':').map(Number)
  return h * 60 + (m || 0)
}

function tutoringHoursToCells(tutoringHours) {
  const cells = new Set()
  ;(tutoringHours || []).forEach((a) => {
    const wday = a.day_of_week % 7
    const dayOrdinal = wday === 0 ? 7 : wday
    const sMin = parseHHMM(a.start_time)
    let eMin = parseHHMM(a.end_time)
    if (eMin <= sMin) eMin = 24 * 60
    for (let m = sMin; m < eMin; m += 15) cells.add(`${dayOrdinal}:${m}`)
  })
  return cells
}

function customerTimesToCells(customerTimes) {
  const cells = new Set()
  ;(customerTimes || []).forEach((r) => {
    const sMin = parseHHMM(r.start_time)
    let eMin = parseHHMM(r.end_time)
    if (eMin <= sMin) eMin = 24 * 60
    for (let m = sMin; m < eMin; m += 15) cells.add(`${r.day_of_week}:${m}`)
  })
  return cells
}

function cellsToApiTimeslots(cells) {
  const fmtApi = (m) => fmtMin(m >= 1440 ? 0 : m)
  const byDay = {}
  for (const key of cells) {
    const [day, mins] = key.split(':').map(Number)
    ;(byDay[day] = byDay[day] || []).push(mins)
  }
  const timeslots = []
  for (const [day, minutes] of Object.entries(byDay)) {
    minutes.sort((a, b) => a - b)
    let start = minutes[0]
    let end = minutes[0] + 15
    for (let i = 1; i < minutes.length; i++) {
      if (minutes[i] === end) {
        end = minutes[i] + 15
      } else {
        timeslots.push({ day_of_week: DAY_NAMES[day], start_time: fmtApi(start), end_time: fmtApi(end) })
        start = minutes[i]
        end = minutes[i] + 15
      }
    }
    timeslots.push({ day_of_week: DAY_NAMES[day], start_time: fmtApi(start), end_time: fmtApi(end) })
  }
  return timeslots
}

// tm2-tutors AvailabilityStep.intersectRanges / subtractRanges
function intersectRanges(rangesA, rangesB) {
  const result = []
  let i = 0
  let j = 0
  while (i < rangesA.length && j < rangesB.length) {
    const start = Math.max(rangesA[i].start, rangesB[j].start)
    const end = Math.min(rangesA[i].end, rangesB[j].end)
    if (start < end) result.push({ start, end })
    if (rangesA[i].end < rangesB[j].end) i++
    else j++
  }
  return result
}
function subtractRanges(ranges, blockRanges) {
  let result = [...ranges]
  for (const block of blockRanges) {
    const next = []
    for (const r of result) {
      if (block.end <= r.start || block.start >= r.end) {
        next.push(r)
      } else {
        if (r.start < block.start) next.push({ start: r.start, end: block.start })
        if (r.end > block.end) next.push({ start: block.end, end: r.end })
      }
    }
    result = next
  }
  return result
}

// tm2-tutors AvailabilityStep.computeMatchingSlots/matchingSlotCells: the cells
// where the client's preferred times overlap the tutor's availability (minus
// existing lessons, for ranges >= lessonDuration). These pre-fill the green
// "Available" selection on open.
function computeMatchingCells(calendar, lessonDuration) {
  if (!calendar) return new Set()
  const toRangesByDay = (arr) => {
    const byDay = {}
    ;(arr || []).forEach((r) => {
      const start = parseHHMM(r.start_time)
      let end = parseHHMM(r.end_time)
      if (end <= start) end = 24 * 60
      ;(byDay[r.day_of_week] = byDay[r.day_of_week] || []).push({ start, end })
    })
    return byDay
  }
  const tutorByDay = toRangesByDay(calendar.tutoring_hours)
  const custByDay = toRangesByDay(calendar.customer_times)
  const lessonsByDay = {}
  ;(calendar.lessons || []).forEach((l) => {
    const s = new Date(l.start_time)
    const e = new Date(l.end_time)
    const dow = s.getDay()
    const dayOrd = dow === 0 ? 7 : dow
    ;(lessonsByDay[dayOrd] = lessonsByDay[dayOrd] || []).push({ start: s.getHours() * 60 + s.getMinutes(), end: e.getHours() * 60 + e.getMinutes() })
  })

  const cells = new Set()
  for (const dayOrdStr of Object.keys(custByDay)) {
    const dayOrd = Number(dayOrdStr)
    const tutorRanges = (tutorByDay[dayOrd] || []).sort((a, b) => a.start - b.start)
    if (tutorRanges.length === 0) continue
    let available = intersectRanges(custByDay[dayOrd].sort((a, b) => a.start - b.start), tutorRanges)
    const dayLessons = (lessonsByDay[dayOrd] || []).sort((a, b) => a.start - b.start)
    if (dayLessons.length > 0) available = subtractRanges(available, dayLessons)
    for (const range of available) {
      if (range.end - range.start >= lessonDuration) {
        for (let m = range.start; m < range.end; m += 15) cells.add(`${dayOrd}:${m}`)
      }
    }
  }
  return cells
}

// tm2-tutors fmtAmPm (drops :00 on the hour)
function fmtAmPm(minutes) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const period = h >= 12 ? 'pm' : 'am'
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, '0')}${period}`
}

// tm2-tutors AvailabilityStep.isTutoringSlotForCell
function isTutoringSlotForCell(tutoringHours, dayOrd, minutes) {
  if (!tutoringHours) return false
  const wday = dayOrd === 7 ? 0 : dayOrd
  return tutoringHours.some((a) => {
    const aDow = a.day_of_week % 7
    if (aDow !== wday) return false
    const sMin = parseHHMM(a.start_time)
    let eMin = parseHHMM(a.end_time)
    if (eMin <= sMin) eMin = 24 * 60
    return minutes >= sMin && minutes < eMin
  })
}

const DAY_SHORT = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

// tm2-tutors AvailabilityStep.mismatchRanges: selected lesson cells that fall
// outside the tutor's profile availability, grouped into contiguous ranges.
function computeMismatchRanges(selectedLessonCells, tutoringHours) {
  const byDay = {}
  for (const key of selectedLessonCells) {
    const [dayOrd, mins] = key.split(':').map(Number)
    if (!isTutoringSlotForCell(tutoringHours, dayOrd, mins)) (byDay[dayOrd] = byDay[dayOrd] || []).push(mins)
  }
  const ranges = []
  const mk = (dayOrd, start, end) => ({ dayOrd, startMin: start, endMin: end, dayLabel: DAY_SHORT[dayOrd], label: `${DAY_SHORT[dayOrd]}: ${fmtAmPm(start)}–${fmtAmPm(end)}` })
  for (const dayOrdStr of Object.keys(byDay)) {
    const dayOrd = Number(dayOrdStr)
    const mins = byDay[dayOrd].sort((a, b) => a - b)
    let start = mins[0]
    let end = mins[0] + 15
    for (let i = 1; i < mins.length; i++) {
      if (mins[i] === end) {
        end = mins[i] + 15
      } else {
        ranges.push(mk(dayOrd, start, end))
        start = mins[i]
        end = mins[i] + 15
      }
    }
    ranges.push(mk(dayOrd, start, end))
  }
  return ranges
}

class TutorCalendar extends React.Component {
  constructor(props) {
    super(props)
    this.weekStart = moment().startOf('isoWeek').format('YYYY-MM-DD')
    this.state = {
      calendar: null,
      editingAvailability: false,
      customerPreferredCells: new Set(),
      selectedLessonCells: new Set(),
      availabilityCells: new Set(),
      loading: true,
      saving: false,
      addingRangeKey: null,
      error: null,
    }
  }

  componentDidMount() {
    this.prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    this.fetch(true)
  }

  componentWillUnmount() {
    document.body.style.overflow = this.prevOverflow || ''
  }

  fetch(seedSelection = false) {
    this.setState({ loading: true, error: null })
    axios
      .get(`/api/sales/tutoring_jobs/${this.props.tutoring_job_id}/tutors/${this.props.tutor_id}/calendar`, {
        params: { week_start: this.weekStart },
      })
      .then((response) => {
        const calendar = response.data
        const next = {
          calendar,
          customerPreferredCells: customerTimesToCells(calendar.customer_times),
          availabilityCells: tutoringHoursToCells(calendar.tutoring_hours),
          loading: false,
        }
        // Seed the green "Available" selection on first open: show the saved
        // job availability if any exists, otherwise suggest the client∩tutor
        // matching slots. (Don't clobber the user's edits on refetch.)
        if (seedSelection) {
          const saved = this.props.jobAvailabilityTimes
          next.selectedLessonCells = saved && saved.length ? savedTimesToCells(saved) : computeMatchingCells(calendar, 60)
        }
        this.setState(next)
      })
      .catch(() => this.setState({ loading: false, error: 'Could not load calendar.' }))
  }

  handleSelectPreferredBlock = ({ keys, selected }) => {
    const field = this.state.editingAvailability ? 'availabilityCells' : 'selectedLessonCells'
    this.setState((s) => {
      const next = new Set(s[field])
      keys.forEach((key) => (selected ? next.add(key) : next.delete(key)))
      return { [field]: next }
    })
  }

  handleResizePreferredBlock = ({ dayOrdinal, oldStartMin, oldEndMin, newStartMin, newEndMin }) => {
    const field = this.state.editingAvailability ? 'availabilityCells' : 'selectedLessonCells'
    this.setState((s) => {
      const next = new Set(s[field])
      for (let m = oldStartMin; m < oldEndMin; m += 15) next.delete(`${dayOrdinal}:${m}`)
      for (let m = newStartMin; m < newEndMin; m += 15) next.add(`${dayOrdinal}:${m}`)
      return { [field]: next }
    })
  }

  startEditing = () => {
    this.setState((s) => ({ editingAvailability: true, availabilityCells: tutoringHoursToCells(s.calendar?.tutoring_hours) }))
  }

  cancelEditing = () => this.setState({ editingAvailability: false })

  headerClose = () => {
    if (this.state.editingAvailability) this.setState({ editingAvailability: false })
    else this.props.onClose()
  }

  // Persist the selected times as this candidate's job availability (recorded
  // against the job candidate) — the same action as the "Set job availability"
  // card. Mirrors the tutor portal's end-of-flow.
  handleConfirm = () => {
    store.emit('tutoring_job:tutor_results:availability:update', {
      tutor_id: this.props.tutor_id,
      tutoring_job_id: this.props.tutoring_job_id,
      timeslots: cellsToApiTimeslots(this.state.selectedLessonCells),
    })
    this.props.onClose()
  }

  // tm2-tutors AvailabilityStep.addRangeToAvailability — add an out-of-availability
  // range to the tutor's profile availability, then refetch so it resolves.
  addRangeToAvailability = (range) => {
    const key = `${range.dayOrd}:${range.startMin}`
    this.setState({ addingRangeKey: key })
    const existing = tutoringHoursToCells(this.state.calendar?.tutoring_hours)
    for (let m = range.startMin; m < range.endMin; m += 15) existing.add(`${range.dayOrd}:${m}`)
    const timeslots = cellsToApiTimeslots(existing)
    axios
      .put(`/api/sales/tutoring_jobs/${this.props.tutoring_job_id}/tutors/${this.props.tutor_id}/availability`, {
        update_availability: { timeslots },
      })
      .then(() => {
        toast.success(`Added to ${this.props.tutor_name}'s availability`)
        this.setState({ addingRangeKey: null })
        this.fetch()
      })
      .catch(() => {
        toast.error('Could not update availability')
        this.setState({ addingRangeKey: null })
      })
  }

  // tm2-tutors AvailabilityStep.removeMismatchRange — drop the whole selected
  // block containing this range.
  removeMismatchRange = (range) => {
    this.setState((s) => {
      const next = new Set(s.selectedLessonCells)
      let blockStart = range.startMin
      while (next.has(`${range.dayOrd}:${blockStart - 15}`)) blockStart -= 15
      let blockEnd = range.endMin
      while (next.has(`${range.dayOrd}:${blockEnd}`)) blockEnd += 15
      for (let m = blockStart; m < blockEnd; m += 15) next.delete(`${range.dayOrd}:${m}`)
      return { selectedLessonCells: next }
    })
  }

  saveAvailability = () => {
    this.setState({ saving: true })
    const timeslots = cellsToApiTimeslots(this.state.availabilityCells)
    axios
      .put(`/api/sales/tutoring_jobs/${this.props.tutoring_job_id}/tutors/${this.props.tutor_id}/availability`, {
        update_availability: { timeslots },
      })
      .then(() => {
        toast.success('Tutor availability updated')
        this.setState({ saving: false, editingAvailability: false })
        this.fetch()
      })
      .catch(() => {
        toast.error('Could not save availability')
        this.setState({ saving: false })
      })
  }

  render() {
    const { calendar, loading, error, saving, editingAvailability, customerPreferredCells, selectedLessonCells, availabilityCells, addingRangeKey } = this.state
    const tutorName = this.props.tutor_name
    const mismatchRanges = !editingAvailability && calendar ? computeMismatchRanges(selectedLessonCells, calendar.tutoring_hours) : []
    const hasMismatch = mismatchRanges.length > 0

    return (
      <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
        <div className="fixed inset-0 bg-black/50" onClick={this.headerClose} />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <div className="relative flex h-[85vh] w-full max-w-[1120px] flex-col rounded-2xl bg-white shadow-xl">
            <div className="flex items-center justify-between px-5 pt-4 pb-2">
              <h3 className="text-base font-semibold text-[#263040]">
                {editingAvailability ? 'Edit Profile Availability' : `Select times ${tutorName} is available for this job`}
              </h3>
              <button onClick={this.headerClose} className="rounded-full bg-gray-100 p-1.5 text-[#64738c] transition-colors hover:bg-gray-200">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {editingAvailability ? (
              <p className="px-5 pb-2 text-sm text-gray-400">Set all the times {tutorName} is available to tutor for <strong>all</strong> jobs.</p>
            ) : (
              <p className="px-5 pb-2 text-sm text-gray-400">
                Match the{' '}
                <span className="stripe-purple inline-block rounded border border-dashed border-purple-300/60 bg-purple-100/50 px-1 py-0.5 text-xs font-semibold leading-none text-purple-600">★ Client's Preferred Times</span>{' '}
                where possible.
              </p>
            )}

            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
              {loading ? (
                <div className="py-16 text-center"><LoaderCircle className="mx-auto size-6 animate-spin text-gray-400" /></div>
              ) : error ? (
                <div className="py-16 text-center text-sm text-red-600">{error}</div>
              ) : (
                <AvailabilityCalendar
                  initialCalendarData={calendar}
                  tutorName={tutorName}
                  mode={editingAvailability ? 'availability' : 'lesson'}
                  customerPreferredCells={editingAvailability ? new Set() : customerPreferredCells}
                  selectedLessonCells={editingAvailability ? new Set() : selectedLessonCells}
                  availabilityCells={availabilityCells}
                  onSelectPreferredBlock={this.handleSelectPreferredBlock}
                  onResizePreferredBlock={this.handleResizePreferredBlock}
                />
              )}
            </div>

            <div className="shrink-0 space-y-3 border-t border-gray-200 px-5 py-3">
              {editingAvailability ? (
                <div className="flex items-center justify-end gap-3">
                  <button onClick={this.cancelEditing} className="rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-[#64738c] transition-colors hover:bg-gray-50">Cancel</button>
                  <button onClick={this.saveAvailability} disabled={saving} className="rounded-full border border-transparent bg-[#263040] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#263040]/90 disabled:opacity-50">{saving ? 'Saving...' : 'Save availability'}</button>
                </div>
              ) : (
                <>
                  {hasMismatch ? (
                    <div className="mx-auto w-fit min-w-[420px] overflow-hidden rounded-xl border border-amber-400 bg-amber-100">
                      <div className="flex items-center gap-2 border-b border-amber-400 px-3 py-2">
                        <svg className="h-4 w-4 shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                        <p className="text-sm font-semibold text-amber-600">These times are outside {tutorName}'s profile availability</p>
                      </div>
                      <div className="divide-y divide-amber-400">
                        {mismatchRanges.map((range) => {
                          const key = `${range.dayOrd}:${range.startMin}`
                          return (
                            <div key={key} className="flex items-center justify-between px-3 py-1.5">
                              <span className="text-sm font-medium text-amber-600">{range.label}</span>
                              <div className="flex shrink-0 items-center gap-2">
                                <button onClick={() => this.addRangeToAvailability(range)} disabled={addingRangeKey === key} className="text-xs font-semibold text-blue-600 underline underline-offset-2 transition-colors hover:text-blue-800 disabled:opacity-50">{addingRangeKey === key ? 'Adding...' : 'Add to profile'}</button>
                                <button onClick={() => this.removeMismatchRange(range)} className="rounded-full bg-amber-200/60 p-1 text-amber-600 transition-colors hover:bg-amber-200">
                                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ) : null}
                  <div className="flex items-center justify-end gap-3">
                    <button onClick={this.startEditing} className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 underline underline-offset-2 transition-colors hover:text-blue-800">
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                      Edit Profile Availability
                    </button>
                    <button onClick={this.handleConfirm} disabled={hasMismatch} className="rounded-full bg-[#263040] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#263040]/90 disabled:cursor-not-allowed disabled:opacity-50">Confirm</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default TutorCalendar
